import { useState } from 'react';
import { Table, Tooltip, Whisper } from 'rsuite';
import CurrencyFormat from 'react-currency-format';
import styles from './DetailingTable.module.scss';
import { DetailsData, DetailsResponse } from '../../types';
import { formatDate, precoStatusMax, precoStatusMin } from '../../utils';
import { LENGTH_MENU } from '../../data';

const { Column, HeaderCell, Cell, Pagination } = Table;

type DetailingTableProps = {
  data: DetailsResponse;
  onChangePage?: (page: number, size: number) => void;
  onChangeLength?: (size: number) => void;
  onSortColumn?: (dataKey: string, sortType: 'asc' | 'desc' | undefined) => void;
};

type SortType = {
  type?: string;
  orderBy?: 'asc' | 'desc' | undefined;
};

export const DetailingTable = ({ data, onChangePage, onChangeLength, onSortColumn }: DetailingTableProps) => {
  const [length, setLength] = useState(20);
  const [sort, setSort] = useState<SortType>({});

  const displayWidth = window.innerWidth;

  const handleLengthChange = (size: number) => {
    setLength(size);
    if (onChangeLength) {
      onChangeLength(size);
    }
  };

  const handlePageChange = (page: number) => {
    onChangePage?.(page, length);
  };

  const handleSortColumn = (dataKey: string, sortType: 'asc' | 'desc' | undefined) => {
    const sortObj = { type: dataKey, orderBy: sortType };
    setSort(sortObj);
    onSortColumn?.(dataKey, sortType);
  };

  return (
    <div>
      <Table
        autoHeight
        bordered
        id="consultar-tabela-comercial"
        className={styles['detailing-table']}
        rowClassName={styles['detailing-table-row']}
        data={data?.content}
        rowHeight={64}
        headerHeight={46}
        sortColumn={sort.type}
        sortType={sort.orderBy}
        onSortColumn={handleSortColumn}
      >
        <Column width={100}>
          <HeaderCell>Período</HeaderCell>
          <Cell dataKey="period" height={60}>
            {rowData => (
              <div className={styles['bold-cell']}>
                <div>
                  <span>{formatDate(rowData.dataInicio)}</span> <span>até</span>
                </div>{' '}
                <span>{formatDate(rowData.dataFinal)}</span>
              </div>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>UF</HeaderCell>
          <Cell dataKey="uf" height={60} />
        </Column>
        <Column width={100}>
          <HeaderCell>GTIN</HeaderCell>
          <Cell dataKey="ean" />
        </Column>
        <Column flexGrow={displayWidth > 1280 ? 2 : 1}>
          <HeaderCell>Descrição</HeaderCell>
          <Cell dataKey="descricao">
            {rowData => (
              <Whisper delayShow={0.2} trigger="hover" placement="bottom" speaker={<Tooltip>{rowData.descricao}</Tooltip>}>
                <p className={styles['main-bold-text']}>{rowData.descricao}</p>
              </Whisper>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Rede</HeaderCell>
          <Cell dataKey="redeResultado" />
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Tipo de Loja</HeaderCell>
          <Cell dataKey="tipoLojaResultado" />
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>
            <span className={styles['flex-column']}>
              <p>Preço</p>
              <p>mínimo</p>
            </span>
          </HeaderCell>
          <Cell dataKey="precoMinimoResultado">
            {(rowData: DetailsData) => (
              <div className={styles['bold-cell']}>
                <div>
                  <Whisper delayShow={0.2} trigger="hover" placement="right" speaker={<Tooltip>Preço praticado mínimo</Tooltip>}>
                    <CurrencyFormat
                      className={styles[precoStatusMin(rowData) ? 'positive' : 'negative']}
                      fixedDecimalScale={true}
                      decimalScale={2}
                      value={rowData.precoMinimoResultado || 0}
                      displayType={'text'}
                      prefix={'R$ '}
                      decimalSeparator={','}
                      thousandSeparator={'.'}
                    />
                  </Whisper>
                </div>
                <Whisper delayShow={0.2} trigger="hover" placement="right" speaker={<Tooltip>Preço cadastrado mínimo</Tooltip>}>
                  <CurrencyFormat
                    className={styles['price-variation']}
                    fixedDecimalScale={true}
                    decimalScale={2}
                    value={rowData.precoMinimoCadastrado || 0}
                    displayType={'text'}
                    prefix={'R$ '}
                    decimalSeparator={','}
                    thousandSeparator={'.'}
                  />
                </Whisper>
              </div>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>
            <span className={styles['flex-column']}>
              <p>Preço</p>
              <p>máximo</p>
            </span>
          </HeaderCell>
          <Cell dataKey="precoMaximoResultado">
            {(rowData: DetailsData) => (
              <div className={styles['bold-cell']}>
                <div>
                  <Whisper delayShow={0.2} trigger="hover" placement="right" speaker={<Tooltip>Preço praticado máximo</Tooltip>}>
                    <CurrencyFormat
                      className={styles[precoStatusMax(rowData) ? 'positive' : 'negative']}
                      fixedDecimalScale={true}
                      decimalScale={2}
                      value={rowData.precoMaximoResultado || 0}
                      displayType={'text'}
                      prefix={'R$ '}
                      decimalSeparator={','}
                      thousandSeparator={'.'}
                    />
                  </Whisper>
                </div>
                <Whisper delayShow={0.2} trigger="hover" placement="right" speaker={<Tooltip>Preço cadastrado máximo</Tooltip>}>
                  <CurrencyFormat
                    className={styles['price-variation']}
                    fixedDecimalScale={true}
                    decimalScale={2}
                    value={rowData.precoMaximoCadastrado || 0}
                    displayType={'text'}
                    prefix={'R$ '}
                    decimalSeparator={','}
                    thousandSeparator={'.'}
                  />
                </Whisper>
              </div>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>
            <span className={styles['flex-column']}>
              <p>Preço</p>
              <p>médio</p>
            </span>
          </HeaderCell>
          <Cell dataKey="precoMedioResultado" className={styles['bold-cell']}>
            {(rowData: DetailsData) => (
              <CurrencyFormat
                fixedDecimalScale={true}
                decimalScale={2}
                value={rowData.precoMedioResultado || 0}
                displayType={'text'}
                prefix={'R$ '}
                decimalSeparator={','}
                thousandSeparator={'.'}
              />
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>
            <span className={styles['flex-column']}>
              <p>Preço</p>
              <p>mais freq.</p>
            </span>
          </HeaderCell>
          <Cell dataKey="precoFrequente" className={styles['bold-cell']}>
            {(rowData: DetailsData) => (
              <CurrencyFormat
                fixedDecimalScale={true}
                decimalScale={2}
                value={rowData.precoFrequente || 0}
                displayType={'text'}
                prefix={'R$ '}
                decimalSeparator={','}
                thousandSeparator={'.'}
              />
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} sortable>
          <HeaderCell>
            <span className={styles['flex-column']}>
              <p>Qtd.</p>
              <p>de preços</p>
            </span>
          </HeaderCell>
          <Cell dataKey="quantidadePreco" className={styles['bold-cell']} />
        </Column>
        <Column width={110} sortable>
          <HeaderCell>
            <span className={styles['flex-column']}>
              <p>Qtd. de preços</p>
              <p>fora do range</p>
            </span>
          </HeaderCell>
          <Cell dataKey="quantidadePrecosForaDoRange" className={styles['bold-cell']} />
        </Column>
        <Column width={110} sortable>
          <HeaderCell>
            <span className={styles['flex-column']}>
              <p>Qtd. lojas fora</p>
              <p>do range</p>
            </span>
          </HeaderCell>
          <Cell dataKey="quantidadeLojasDistintas" className={styles['bold-cell']} />
        </Column>
      </Table>
      <Pagination
        activePage={data?.page?.number + 1 || null}
        displayLength={length}
        total={data?.page?.totalElements || null}
        onChangePage={handlePageChange}
        onChangeLength={handleLengthChange}
        lengthMenu={LENGTH_MENU}
      />
    </div>
  );
};
